import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Download, Upload } from 'lucide-react';
import { useData } from '../App';
import { Button, Card } from '../components/UI';
import { api } from '../services/api';
import { formatMoney } from '../utils/format';

type Row = { row: number; date: string | null; organizationName: string; kind: string | null; type: string | null; amount: number | null;
             paymentMethod: string; counterpartyName: string; description: string; errors: string[]; warnings: string[] };

const KIND_LABELS: Record<string, string> = { REGULAR: 'Обычная', SALARY: 'ЗП офиц.', UNOFFICIAL_SALARY: 'ЗП неофиц.', TAX: 'Налог (ЕНС)',
                                              OWNER_WITHDRAWAL: 'Вывод себе', NON_TAX: 'Займ/перевод' };

// Импорт операций из Excel/CSV: шаблон → предпросмотр с ошибками → загрузка строк без ошибок
export const ImportPage: React.FC = () => {
    const { refreshData, showNotification } = useData();
    const [rows, setRows] = useState<Row[] | null>(null);
    const [busy, setBusy] = useState(false);

    const preview = async (file: File) => {
        setBusy(true);
        try { setRows((await api.importPreview(file)).rows); }
        catch (e: any) { showNotification(e.message || 'Не удалось прочитать файл', 'error'); }
        finally { setBusy(false); }
    };
    const commit = async () => {
        if (!rows) return;
        setBusy(true);
        try {
            const r = await api.importCommit(rows);
            showNotification(`Загружено операций: ${r.created}`, 'success');
            setRows(null); refreshData();
        } catch (e: any) { showNotification(e.message || 'Ошибка импорта', 'error'); }
        finally { setBusy(false); }
    };
    const good = rows?.filter(r => r.errors.length === 0).length ?? 0;

    return (
        <div className="space-y-4">
            <Link to="/reports" className="text-sm text-blue-600 flex items-center gap-1"><ArrowLeft size={14} /> Отчёты</Link>
            <h1 className="text-2xl font-bold text-slate-900">Импорт операций из Excel</h1>
            <Card className="bg-white">
                <div className="space-y-3 text-sm">
                    <p>Колонки: <strong>Дата; ИП; Тип; Сумма; Нал/безнал; Контрагент; Объект; Комментарий</strong>. ИП — фамилия или ИНН. Тип: Приход, Расход, Самозанятый, ЗП офиц, ЗП неофиц, Налог, Вывод себе, Займ.</p>
                    <div className="flex flex-wrap gap-2">
                        <a href={api.importTemplateUrl()} className="inline-flex items-center px-3 py-2 rounded-md border border-slate-300 text-sm hover:bg-slate-50"><Download className="mr-2 h-4 w-4" /> Шаблон CSV</a>
                        <label className="inline-flex items-center px-3 py-2 rounded-md bg-blue-600 text-white text-sm cursor-pointer hover:bg-blue-700">
                            <Upload className="mr-2 h-4 w-4" /> Выбрать файл (CSV или XLSX)
                            <input type="file" accept=".csv,.xlsx" className="hidden" onChange={e => { if (e.target.files?.[0]) preview(e.target.files[0]); e.target.value = ''; }} />
                        </label>
                    </div>
                </div>
            </Card>
            {rows && (
                <>
                    <div className="flex justify-between items-center text-sm">
                        <span>Строк: {rows.length} · без ошибок: <strong className="text-emerald-700">{good}</strong> · с ошибками (не загрузятся): <strong className="text-red-600">{rows.length - good}</strong></span>
                        <Button onClick={commit} disabled={busy || good === 0}>Загрузить {good}</Button>
                    </div>
                    <div className="bg-white rounded-lg border border-slate-200 overflow-x-auto">
                        <table className="w-full text-xs">
                            <thead className="bg-slate-50 text-slate-600"><tr>
                                <th className="px-2 py-2 text-left">Стр.</th><th className="px-2 py-2 text-left">Дата</th><th className="px-2 py-2 text-left">ИП</th>
                                <th className="px-2 py-2 text-left">Вид</th><th className="px-2 py-2 text-right">Сумма</th><th className="px-2 py-2 text-left">Оплата</th>
                                <th className="px-2 py-2 text-left">Контрагент</th><th className="px-2 py-2 text-left">Комментарий</th><th className="px-2 py-2 text-left">Проверка</th>
                            </tr></thead>
                            <tbody className="divide-y divide-slate-100">
                                {rows.map(r => (
                                    <tr key={r.row} className={r.errors.length ? 'bg-red-50' : r.warnings.length ? 'bg-amber-50/60' : ''}>
                                        <td className="px-2 py-1">{r.row}</td><td className="px-2 py-1">{r.date || '—'}</td><td className="px-2 py-1">{r.organizationName}</td>
                                        <td className="px-2 py-1">{r.type === 'INCOME' ? 'Приход' : r.kind ? KIND_LABELS[r.kind] || r.kind : '—'}</td>
                                        <td className="px-2 py-1 text-right font-mono">{r.amount !== null ? formatMoney(r.amount) : '—'}</td>
                                        <td className="px-2 py-1">{r.paymentMethod === 'cash' ? 'нал' : 'безнал'}</td>
                                        <td className="px-2 py-1">{r.counterpartyName}</td><td className="px-2 py-1">{r.description}</td>
                                        <td className="px-2 py-1">{r.errors.map(e => <div key={e} className="text-red-700">✕ {e}</div>)}{r.warnings.map(w => <div key={w} className="text-amber-700">! {w}</div>)}{!r.errors.length && !r.warnings.length && <span className="text-emerald-700">OK</span>}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </>
            )}
        </div>
    );
};
